import { useState, useEffect, useContext, useCallback } from 'react';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Image,
    Spinner,
    Alert,
    Table,
    Button,
    OverlayTrigger,
    Tooltip,
    Badge,
    Form,
    Modal,
} from 'react-bootstrap';
import Pagination from 'react-bootstrap/Pagination';
import { useMediaQuery } from 'react-responsive';
import Sidebar from './Sidebar';
import Breadcrumb from './Breadcrumb';
import icNoInsights from './assets/img/ic-empty-box.svg';
import icSuccess from './assets/img/ic-celebration-circle.svg';
import icAdd from './assets/img/ic-add-circle.svg';
import useMobileLoadMore from './components/Discovery/useMobileLoadMore';
import MobileLoadMoreFooter from './components/Discovery/MobileLoadMoreFooter';
import MobileSupportRequestCard from './components/Support/MobileSupportRequestCard';
import ProfileUpdateSuccessModal from './components/Profile/ProfileUpdateSuccessModal';
import { useMobileHeaderBackAction } from './HeaderActionContext';

const TYPES = ['Incident', 'Support service request', 'Advisory service request'];

const STATUS_BG = { Resolved: 'info', Open: 'active', Archived: 'inactive' };

const getReplyInfo = (ticket, userId) => {
    const lastRead = parseInt(localStorage.getItem(`support_read_${ticket._id}`) || '0', 10);
    const studentId = String(userId || '');
    const adminReplies = (ticket.activity || []).filter(a =>
        a.public && a.createdBy && String(a.createdBy) !== studentId
    );
    const hasNew = adminReplies.some(a => new Date(a.createdAt).getTime() > lastRead);
    return { count: adminReplies.length, hasNew };
};

const SupportList = () => {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const handleMobileBack = useCallback(() => navigate('/discovery/countries'), [navigate]);
    useMobileHeaderBackAction(handleMobileBack, isMobile);

    const [requests, setRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ type: '', title: '', description: '' });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [attachments, setAttachments] = useState([]);

    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusModalContent, setStatusModalContent] = useState({
        title: 'Support request submitted',
        message: 'Your request was successfully submitted. We will get back to you soon.',
    });

    useEffect(() => {
        if (location.state?.supportSuccess !== 'deleted') return;

        if (isMobile) {
            setStatusModalContent({
                title: 'Request deleted',
                message: 'Your support request was successfully deleted.',
            });
            setShowStatusModal(true);
        }

        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }, [isMobile, location.pathname, location.search, location.state, navigate]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            setError('');
            const res = await api.get('/api/support-requests/my');
            const list = Array.isArray(res.data) ? res.data : [];
            setRequests(list);
            setFilteredRequests(list);
        } catch (err) {
            setError('An error occurred while fetching support requests.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) fetchRequests();
    }, [user]);

    useEffect(() => {
        const q = searchQuery.trim().toLowerCase();
        const next = requests.filter(r => {
            if (!q) return true;
            return [r.ticketId, r.businessPurpose, r.ticketClass, r.comment]
                .map(v => String(v || '').toLowerCase())
                .some(s => s.includes(q));
        });
        setFilteredRequests(next);
        setCurrentPage(1);
    }, [searchQuery, requests]);

    const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentItems = filteredRequests.slice(indexOfFirst, indexOfLast);

    const mobileSupportPagination = useMobileLoadMore({
        items: filteredRequests,
        filterKey: searchQuery.trim().toLowerCase(),
        storageKey: 'support-requests-mobile',
        isMobile,
    });

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleOpenModal = () => {
        setFormData({ type: '', title: '', description: '' });
        setFormErrors({});
        setSubmitError('');
        setAttachments([]);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};
        if (!formData.type) newErrors.type = 'Type is required';
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';

        if (Object.keys(newErrors).length > 0) {
            setFormErrors(newErrors);
            return;
        }

        const MAX = 5 * 1024 * 1024;
        if (attachments.some(f => f.size > MAX)) {
            setSubmitError('One or more files exceed 5MB. Please remove them.');
            return;
        }

        setSubmitting(true);
        setSubmitError('');
        try {
            const body = new FormData();
            body.append('type', formData.type);
            body.append('title', formData.title);
            body.append('description', formData.description);
            attachments.forEach(f => body.append('attachments', f));
            await api.post('/api/support-requests', body);
            setShowModal(false);
            setAttachments([]);
            await fetchRequests();
            setStatusModalContent({
                title: 'Support request submitted',
                message: 'Your request was successfully submitted. We will get back to you soon.',
            });
            setShowStatusModal(true);
        } catch (err) {
            setSubmitError(err.response?.data?.message || 'An error occurred. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">{error}</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Card className={isMobile ? 'mb-4' : undefined}>
                                <Card.Body>
                                    <Row>
                                        <Col xs={12}>
                                          {isMobile ? (
                                            <>
                                              <h3 className="company-detail-section-title">Support requests</h3>
                                              <hr />
                                              <Button
                                                  variant="primary"
                                                  className="mobile-support-add"
                                                  onClick={handleOpenModal}
                                              >
                                                  New support request
                                              </Button>

                                              {filteredRequests.length === 0 ? (
                                                  <Row>
                                                      <Col className="no-result d-flex flex-column justify-content-center align-items-center text-center">
                                                          <Image src={icNoInsights} alt="No requests" className="mb-4" />
                                                          <h2 className="mb-3">No support requests yet.</h2>
                                                          <p className="mb-4">Your support requests will appear here.</p>
                                                      </Col>
                                                  </Row>
                                              ) : (
                                                  <div className="mobile-support-list">
                                                      {mobileSupportPagination.mobileItems.map(r => (
                                                          <MobileSupportRequestCard
                                                              key={r._id}
                                                              request={r}
                                                              replyInfo={getReplyInfo(r, user?._id)}
                                                              formatDate={formatDate}
                                                              onOpen={() => navigate(`/support/${r._id}`)}
                                                          />
                                                      ))}
                                                      <MobileLoadMoreFooter
                                                          visibleCount={mobileSupportPagination.visibleCount}
                                                          totalCount={mobileSupportPagination.totalCount}
                                                          itemLabel="requests"
                                                          onLoadMore={mobileSupportPagination.loadMore}
                                                          isLoading={mobileSupportPagination.isLoadingMore}
                                                          hasMore={mobileSupportPagination.hasMore}
                                                      />
                                                  </div>
                                              )}
                                            </>
                                          ) : (
                                            <>
                                            <Row className="align-items-center">
                                                <Col>
                                                    <h2 className="mb-4">Support requests</h2>
                                                </Col>
                                            </Row>


                                            {filteredRequests.length === 0 ? (
                                                <Row>
                                                    <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                                        <Image src={icNoInsights} alt="No requests" className="mb-4" />
                                                        <h2 className="mb-3">No support requests yet.</h2>
                                                        <p className="mb-4">Your support requests will appear here.</p>
                                                    </Col>
                                                </Row>
                                            ) : (
                                                <>
                                                    <Row>
                                                        <Col>
                                                            <div className="table-responsive list">
                                                                <Table>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>ID</th>
                                                                            <th>Date</th>
                                                                            <th>Type</th>
                                                                            <th>Title</th>
                                                                            <th>Description</th>
                                                                            <th>Replies</th>
                                                                            <th>Status</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {currentItems.map(r => {
                                                                            const desc = r.comment || '';
                                                                            const isLong = desc.length > 60;
                                                                            const preview = isLong ? `${desc.slice(0, 60)}…` : desc || '—';

                                                                            return (
                                                                                <tr key={r._id} onClick={() => navigate(`/support/${r._id}`)} style={{ cursor: 'pointer' }}>
                                                                                    <td>#{r.ticketId}</td>
                                                                                    <td>{formatDate(r.createdAt)}</td>
                                                                                    <td>{r.ticketClass}</td>
                                                                                    <td>{r.businessPurpose || '—'}</td>
                                                                                    <td>
                                                                                        {isLong ? (
                                                                                            <OverlayTrigger
                                                                                                placement="top"
                                                                                                overlay={<Tooltip style={{ maxWidth: 300 }}>{desc}</Tooltip>}
                                                                                            >
                                                                                                <span style={{ cursor: 'pointer' }}>{preview}</span>
                                                                                            </OverlayTrigger>
                                                                                        ) : preview}
                                                                                    </td>
                                                                                    <td>
                                                                                        {(() => {
                                                                                            const { count, hasNew } = getReplyInfo(r, user?._id);
                                                                                            if (count === 0) return '—';
                                                                                            return (
                                                                                                <>
                                                                                                    {count}
                                                                                                    {hasNew && (
                                                                                                        <span className="badge rounded-pill ms-1 badge-new">NEW</span>
                                                                                                    )}
                                                                                                </>
                                                                                            );
                                                                                        })()}
                                                                                    </td>
                                                                                    <td>
                                                                                        <OverlayTrigger
                                                                                            placement="top"
                                                                                            overlay={<Tooltip id={`tooltip-status-${r._id}`}>{r.status || 'Open'}</Tooltip>}
                                                                                        >
                                                                                            <Badge bg={STATUS_BG[r.status] || 'active'}>&nbsp;</Badge>
                                                                                        </OverlayTrigger>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <Row className="pagination-wrapper">
                                                        <Col className="d-flex align-items-center">
                                                            <span>Total&nbsp;{filteredRequests.length}&nbsp;requests</span>
                                                            <Pagination>
                                                                <Pagination.Prev
                                                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                                    disabled={currentPage === 1}
                                                                />
                                                                {[...Array(totalPages).keys()].map(number => (
                                                                    <Pagination.Item
                                                                        key={number + 1}
                                                                        active={number + 1 === currentPage}
                                                                        onClick={() => setCurrentPage(number + 1)}
                                                                    >
                                                                        {number + 1}
                                                                    </Pagination.Item>
                                                                ))}
                                                                <Pagination.Next
                                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                                    disabled={currentPage === totalPages}
                                                                />
                                                            </Pagination>
                                                        </Col>
                                                        <Col className="pagination-controls">
                                                            <div className="pagination-controls-per-page">
                                                                <Form.Select
                                                                    value={rowsPerPage}
                                                                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                                                    style={{ width: 'auto', marginRight: '10px' }}
                                                                >
                                                                    <option value="10">10 / page</option>
                                                                    <option value="20">20 / page</option>
                                                                    <option value="40">40 / page</option>
                                                                </Form.Select>
                                                            </div>
                                                            <label className="pagination-controls-goto-label" htmlFor="goto-page-support">Go to</label>
                                                            <Form.Control
                                                                id="goto-page-support"
                                                                type="number"
                                                                className="pagination-controls-goto-input"
                                                                value={currentPage}
                                                                onChange={(e) => {
                                                                    const p = Number(e.target.value);
                                                                    if (p >= 1 && p <= totalPages) setCurrentPage(p);
                                                                }}
                                                                onBlur={() => {
                                                                    if (currentPage < 1) setCurrentPage(1);
                                                                    if (currentPage > totalPages) setCurrentPage(totalPages);
                                                                }}
                                                                style={{ textAlign: 'center' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </>
                                            )}
                                            </>
                                          )}
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {!isMobile && (
                        <Row className="buttons-row">
                            <Col>
                                <Button variant="primary" onClick={handleOpenModal}>
                                    New support request
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Container>
            </main>

            {/* New support request modal */}
            <Modal
                className={isMobile ? 'with-form discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen support-mobile-form-modal' : 'with-form'}
                show={showModal}
                onHide={() => setShowModal(false)}
                centered={!isMobile}
                fullscreen={isMobile}
            >
                <Form onSubmit={handleSubmit} className={isMobile ? 'discovery-filter-mobile-panel' : undefined}>
                    {isMobile ? (
                        <div className="discovery-filter-mobile-panel__header">
                            <Modal.Title>New support request</Modal.Title>
                            <div className="discovery-filter-mobile-panel__header-actions">
                                <button
                                    type="button"
                                    className="discovery-filter-mobile-panel__close"
                                    aria-label="Close support request form"
                                    onClick={() => setShowModal(false)}
                                >&times;</button>
                            </div>
                        </div>
                    ) : (
                        <Modal.Header closeButton>
                            <Modal.Title>New support request</Modal.Title>
                        </Modal.Header>
                    )}
                    <Modal.Body>
                        {submitError && <Alert variant="danger">{submitError}</Alert>}
                        <Row className="mb-3">
                            <Col>
                                <Form.Group controlId="supportType">
                                    <Form.Label>Type <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleFormChange}
                                        isInvalid={!!formErrors.type}
                                    >
                                        <option value="">Please select</option>
                                        {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{formErrors.type}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                <Form.Group controlId="supportTitle">
                                    <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        name="title"
                                        value={formData.title}
                                        onChange={handleFormChange}
                                        isInvalid={!!formErrors.title}
                                        placeholder="Brief summary of your issue"
                                    />
                                    <Form.Control.Feedback type="invalid">{formErrors.title}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group controlId="supportDescription">
                                    <Form.Label>Description <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleFormChange}
                                        isInvalid={!!formErrors.description}
                                        placeholder="Describe your issue in detail"
                                    />
                                    <Form.Control.Feedback type="invalid">{formErrors.description}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <Form.Group controlId="supportAttachments">
                                    <Form.Label>Attachments</Form.Label>

                                    {attachments.length < 3 && (
                                        <div
                                            className="custom-upload-box mb-1"
                                            onClick={() => document.getElementById('supportFileInput').click()}
                                        >
                                            <Image className="upload-icon" src={icAdd} alt="Upload file" />
                                            <span className="upload-text">
                                                Choose file{attachments.length > 0 && ` (${attachments.length}/3)`}
                                            </span>
                                        </div>
                                    )}

                                    <Form.Control
                                        id="supportFileInput"
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            const added = Array.from(e.target.files);
                                            setAttachments(prev => [...prev, ...added].slice(0, 3));
                                            e.target.value = '';
                                        }}
                                    />

                                    <p className="info">JPG, PNG, PDF or Word. Max 5MB each. Up to 3 files.</p>

                                    {attachments.length > 0 && (() => {
                                        const MAX = 5 * 1024 * 1024;
                                        const hasOversized = attachments.some(f => f.size > MAX);
                                        return (
                                            <>
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                    {attachments.map((file, idx) => {
                                                        const isImg = /\.(jpg|jpeg|png)$/i.test(file.name);
                                                        const tooLarge = file.size > MAX;
                                                        return (
                                                            <div key={idx} style={{ position: 'relative' }}>
                                                                {isImg ? (
                                                                    <Image
                                                                        src={URL.createObjectURL(file)}
                                                                        width={64}
                                                                        height={64}
                                                                        style={{
                                                                            objectFit: 'cover', borderRadius: 6,
                                                                            border: tooLarge ? '2px solid #dc3545' : '1px solid #c0d9ee',
                                                                            opacity: tooLarge ? 0.6 : 1,
                                                                            display: 'block',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div style={{
                                                                        width: 64, height: 64, borderRadius: 6,
                                                                        border: tooLarge ? '2px solid #dc3545' : '1px solid #c0d9ee',
                                                                        background: '#f0f8ff', display: 'flex',
                                                                        alignItems: 'center', justifyContent: 'center',
                                                                        padding: 4, opacity: tooLarge ? 0.6 : 1,
                                                                    }}>
                                                                        <span style={{ fontSize: 9, wordBreak: 'break-all', textAlign: 'center', lineHeight: 1.2 }}>
                                                                            {file.name}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {tooLarge && (
                                                                    <div style={{
                                                                        position: 'absolute', bottom: 0, left: 0, right: 0,
                                                                        background: 'rgba(220,53,69,0.85)', color: '#fff',
                                                                        fontSize: 9, textAlign: 'center',
                                                                        borderRadius: '0 0 6px 6px', padding: '1px 0', lineHeight: 1.4,
                                                                    }}>Too large</div>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                                                                    style={{
                                                                        position: 'absolute', top: -6, right: -6,
                                                                        width: 18, height: 18, borderRadius: '50%',
                                                                        background: '#dc3545', border: 'none', color: '#fff',
                                                                        fontSize: 11, lineHeight: 1, cursor: 'pointer',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    }}
                                                                >×</button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {hasOversized && (
                                                    <p className="text-danger mt-2 mb-0" style={{ fontSize: '1.2rem' }}>
                                                        Some files exceed 5MB. Please remove them before saving.
                                                    </p>
                                                )}
                                            </>
                                        );
                                    })()}
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    {isMobile ? (
                        <div className="discovery-filter-mobile-panel__footer support-mobile-form-modal__footer">
                            <Button variant="primary" type="submit" disabled={submitting || attachments.some(f => f.size > 5 * 1024 * 1024)}>
                                {submitting ? 'Saving…' : 'Save'}
                            </Button>
                        </div>
                    ) : (
                        <Modal.Footer>
                            <Button variant="primary" type="submit" disabled={submitting || attachments.some(f => f.size > 5 * 1024 * 1024)}>
                                {submitting ? 'Saving…' : 'Save'}
                            </Button>
                        </Modal.Footer>
                    )}
                </Form>
            </Modal>

            {/* Success modal */}
            {isMobile ? (
                <ProfileUpdateSuccessModal
                    show={showStatusModal}
                    onHide={() => setShowStatusModal(false)}
                    title={statusModalContent.title}
                    message={statusModalContent.message}
                    icon={icSuccess}
                />
            ) : (
                <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                    <Modal.Header closeButton />
                    <Modal.Body>
                        <Image src={icSuccess} alt="" className="d-block m-auto mb-4" />
                        <h2 className="text-center mb-3">Support request submitted</h2>
                        <p className="text-center">Your request was successfully submitted. We will get back to you soon.</p>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-center">
                        <Button variant="primary" onClick={() => setShowStatusModal(false)}>Go back to Support</Button>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};

export default SupportList;
